import { useId } from "react"
import { shortDate } from "../answer/format"
import { fileState } from "./fileStatus"
import { composition, demandLine, type SessionKind } from "./proceedings"
import type { ProceedingChoice as Choice } from "./useProceedingChoice"
import "./ProceedingChoice.css"

const QUESTION: Record<SessionKind, string> = {
  oio: "Which show cause notice is this order for?",
  notice: "Which show cause notice?",
  oia: "Which order is the appeal against?",
  appeal: "Which order is the appeal against?",
}

/** "Waiting for 2 files to finish reading…", each with where it is. Nothing when nothing is being read. */
export function ReadingWait({ choice }: { choice: Choice }) {
  const n = choice.reading.length
  if (!n) return null
  return (
    <div className="pcx-wait" role="status">
      <p>{`Waiting for ${n} ${n === 1 ? "file" : "files"} to finish reading…`}</p>
      <ul>{choice.reading.map((f) => <li key={f.doc_id}><span className="pcx-fn">{f.name}</span><span>{fileState(f).label}</span></li>)}</ul>
    </div>
  )
}

/** A radio list of the proceedings to start from. Nothing to choose with fewer than two. */
export function ProceedingRadios({ choice }: { choice: Choice }) {
  const name = useId()
  if (choice.options.length < 2) return null
  return (
    <fieldset className="pcx-set">
      <legend>{QUESTION[choice.kind]}</legend>
      <ul className="pcx-list">
        {choice.options.map((g) => {
          const p = g.proceeding
          const id = `${name}-${p.id}`
          const sub = [p.date ? shortDate(p.date) : "", demandLine(p), composition(g)].filter(Boolean).join(" · ")
          return (
            <li key={p.id}>
              <input type="radio" id={id} name={name} checked={choice.chosen?.proceeding.id === p.id} onChange={() => choice.choose(p.id)} />
              <label htmlFor={id}><span className="pcx-t">{p.title}</span>{sub ? <span className="pcx-s">{sub}</span> : null}</label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}

/** The chosen proceeding's papers, each uncheckable, and the others behind "Also include". */
export function FilesChecklist({ choice }: { choice: Choice }) {
  const name = useId()
  if (!choice.chosen) return null
  const box = (docId: string, text: string, date: string | undefined, file: string, hint?: string) => {
    const id = `${name}-${docId}`
    return (
      <li key={docId}>
        <input type="checkbox" id={id} checked={choice.isChecked(docId)} onChange={() => choice.toggle(docId)} />
        <label htmlFor={id}>
          <span className="pcx-t">{text}{date ? <span className="pcx-d"> · {shortDate(date)}</span> : null}</span>
          <span className="pcx-s">{hint ? `${hint} · ` : ""}{file}</span>
        </label>
      </li>
    )
  }
  return (
    <>
      <fieldset className="pcx-set">
        <legend>Using these files</legend>
        <ul className="pcx-list">{choice.members.map((m) => box(m.file.doc_id, m.label, m.date, m.file.name))}</ul>
      </fieldset>
      {choice.extras.length ? (
        <details className="pcx-more">
          <summary>{`Also include (${choice.extras.length})`}</summary>
          <ul className="pcx-list">{choice.extras.map((x) => box(x.file.doc_id, x.hint ? x.file.name : x.file.name, undefined, x.hint ?? "Evidence", undefined))}</ul>
        </details>
      ) : null}
    </>
  )
}
